import { Params, redirect } from 'react-router-dom';
import {
  activateConnectorCloudFunction,
  createConnectorCloudFunction,
  updateShopCloudFunction,
} from '@/firebase/init';
import { OrganizationFromStore } from '@/types/Organization';
import { ConnectorCategory, ValidConnector } from '@/types/Connectors';
import { useOrganizationStore } from '@/stores/organizationStore';
import { UserStateData } from '@/types/types';
import { assert } from '@/utils/asserts';
import { ConnectorsService } from '@/services/connectorsService';
import { TagsService } from '@/services/dataforseo/tagsService';
import { KeywordsService } from '@/services/dataforseo/keywordsService';
import { serverTimestamp } from 'firebase/firestore';
export function connectorEditLoader(
  connectorType: ValidConnector,
  connectorCustomName: string,
  connectorCategory: ConnectorCategory = 'advertising',
) {
  if (connectorType === 'shopify') {
    return shopifyEditLoader;
  } else if (connectorType === 'dataforseo') {
    return dataforseoEditLoader;
  }

  return defaultEditLoader;

  async function shopifyEditLoader(params: Params, request: Request) {
    const connectorName = params.connectorName as string;
    const queryParams = new URL(request.url).searchParams;
    const workspaceId = queryParams.get('workspaceId') as string;
    const state = queryParams.get('state');
    const shopDomain = queryParams.get('shopDomain') as string;
    const organization = useOrganizationStore.getState()
      .selectedOrganization as OrganizationFromStore;
    if (!state) {
      return await ConnectorsService.getConnector(workspaceId, connectorName);
    }
    await createConnectorCloudFunction({
      connectorId: connectorName,
      workspaceId,
      organizationId: organization.id,
      region: organization.region,
    });

    await updateShopCloudFunction({
      connectorId: connectorName,
      workspaceId,
      shop: shopDomain,
      region: organization.region,
      organizationId: organization.id,
    });

    return redirect(
      `/connectors/edit/${connectorName}?workspaceId=${workspaceId}&status=success`,
    );
  }

  async function dataforseoEditLoader(
    params: Params,
    request: Request,
    user: UserStateData,
  ) {
    const connectorName = params.connectorName as string;
    const queryParams = new URL(request.url).searchParams;
    const timezone = queryParams.get('timezone') as string;
    const locationCode = queryParams.get('location') as string;
    const workspaceId = queryParams.get('workspaceId') as string;
    const organization = useOrganizationStore.getState()
      .selectedOrganization as OrganizationFromStore;
    const oauthId = '';
    const connector = await ConnectorsService.getConnector(workspaceId, connectorName);
    if (connector) {
      const tags = await TagsService.getAllTags(workspaceId, connectorName);
      const firstPageKeywords = await KeywordsService.getFirstPage(
        workspaceId,
        connectorName,
      );
      return { connector, tags, keywords: firstPageKeywords };
    } else {
      const res = await createConnectorCloudFunction({
        connectorId: connectorName,
        workspaceId,
        organizationId: organization.id,
        region: organization.region,
        timezone,
        locationCode: parseInt(locationCode),
      });
      await ConnectorsService.addConnector(
        workspaceId,
        connectorName,
        {
          type: connectorType,
          name: connectorCustomName,
          category: connectorCategory,
          config: {
            accounts: [
              {
                name: 'dataforseo',
                id: 'dataforseo',
                selected: true,
                status: 'ACCOUNT_DELETION_SUCCESS',
              },
            ],
          },
          timezone,
          locationCode: parseInt(locationCode),
        },
        user.email as string,
        organization.id,
        oauthId,
        true, // should always be deactivated
      );
      if (!res.data.isCreatingOrganizationConnector) {
        await activateConnectorCloudFunction({
          connectorId: connectorName,
          organizationId: organization.id,
          region: organization.region,
          workspaceId,
          timezone,
          locationCode: parseInt(locationCode),
        });
      }
      const createdConnector = await ConnectorsService.getConnector(
        workspaceId,
        connectorName,
      );
      return { connector: createdConnector, tags: [], keywords: [] };
    }
  }

  async function defaultEditLoader(
    params: Params,
    request: Request,
    user: UserStateData,
  ) {
    const connectorName = params.connectorName as string;
    const queryParams = new URL(request.url).searchParams;
    const workspaceId = queryParams.get('workspaceId') as string;
    const reconnect = !!queryParams.get('reconnect') || false;
    const state = queryParams.get('state');
    const organization = useOrganizationStore.getState()
      .selectedOrganization as OrganizationFromStore;
    const oauthId = queryParams.get('id') as string;
    if (!state) {
      return await ConnectorsService.getConnector(workspaceId, connectorName);
    }
    const res = await createConnectorCloudFunction({
      connectorId: connectorName,
      workspaceId,
      organizationId: organization.id,
      region: organization.region,
    });
    assert(state === 'success' && state !== null, 'Something went wrong');

    if (!reconnect) {
      const shouldBeDeactivated = res.data.isCreatingOrganizationConnector;

      await ConnectorsService.updateConnector(
        workspaceId,
        connectorName,
        {
          created_date: serverTimestamp(),
          created_by: user.email,
          type: connectorType,
          name: connectorCustomName,
          category: connectorCategory,
          organizationId: organization.id,
          oauthId,
          isInitialSetup: shouldBeDeactivated,
          connectorId: connectorName,
          status: shouldBeDeactivated ? 'DEACTIVATION_SUCCESS' : 'ACTIVATION_SUCCESS',
        },
        user.email as string,
      );
    }

    return redirect(
      `/connectors/edit/${connectorName}?workspaceId=${workspaceId}&status=success`,
    );
  }
}
